import { Icon, Pill, Text } from '@/design-system'
import { asset } from '@/lib/assets'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'
import type { Card as CardModel } from '@/data/types'
import { BRAND } from '@/brand/brand'
import styles from './Plastic.module.css'

function Contactless() {
  return (
    <svg
      className={styles.contactless}
      width="20"
      height="24"
      viewBox="0 0 20 24"
      fill="none"
      aria-hidden="true"
    >
      {[0, 1, 2, 3].map((i) => (
        <path
          key={i}
          d={`M${2 + i * 4.6} ${7 - i * 1.6}a${5 + i * 4.6} ${5 + i * 4.6} 0 0 1 0 ${10 + i * 3.2}`}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      ))}
    </svg>
  )
}

function NetworkMark({ network }: { readonly network: CardModel['network'] }) {
  if (network === 'mastercard') {
    return (
      <svg
        className={styles.network}
        width="46"
        height="28"
        viewBox="0 0 46 28"
        aria-label="Mastercard"
      >
        <circle cx="17" cy="14" r="9.5" fill="#eb001b" />
        <circle cx="29" cy="14" r="9.5" fill="#f79e1b" />
        <path d="M23 6.6a9.48 9.48 0 0 0 0 14.8 9.48 9.48 0 0 0 0-14.8Z" fill="#ff5f00" />
      </svg>
    )
  }
  return (
    <Text variant="subtitle" weight="bold" tone="onDark" className={styles.network}>
      VISA
    </Text>
  )
}

const KIND_LABEL: Record<CardModel['kind'], string> = {
  physical: 'Physical',
  virtual: 'Virtual',
  agent: 'Agent',
}

export function Plastic({ card }: { readonly card: CardModel }) {
  const groups = ['••••', '••••', '••••', card.last4]
  const isPlastic = card.kind === 'physical'

  return (
    <div
      className={cn(
        styles.plastic,
        card.kind === 'virtual' && styles.virtual,
        card.kind === 'agent' && styles.agent,
        card.status === 'frozen' && styles.frozen,
      )}
    >
      {card.kind === 'agent' && <div className={styles.lattice} aria-hidden="true" />}

      <div className={styles.row}>
        <div className={styles.brandRow}>
          <img className={styles.mark} src={asset(BRAND.mark)} alt="" />
          <span className={styles.wordmark}>{BRAND.wordmark}</span>
        </div>
        <Pill tone="onDark">
          {card.status === 'frozen' ? 'Frozen' : KIND_LABEL[card.kind]}
        </Pill>
      </div>

      <div className={styles.bottom}>
        {isPlastic ? (
          <div className={styles.chipRow}>
            <div className={styles.chip} aria-hidden="true" />
            <Contactless />
          </div>
        ) : card.kind === 'agent' && card.agent ? (
          <div className={styles.policy}>
            <div className={styles.policyRow}>
              <Text variant="eyebrow" tone="onDarkMuted">
                Per transaction
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark" numeric>
                {formatMoney(card.agent.perTransaction)}
              </Text>
            </div>
            <div className={styles.policyRow}>
              <Text variant="eyebrow" tone="onDarkMuted">
                {card.agent.merchants.length} merchants · {card.agent.transactionsLeft} left
              </Text>
              <Text variant="eyebrow" weight="semibold" tone="accent">
                Revocable
              </Text>
            </div>
          </div>
        ) : (
          <span className={styles.online}>
            <Icon name="language" size={14} />
            Online only
          </span>
        )}

        <span className={styles.number} aria-label={`Card ending ${card.last4}`}>
          {groups.map((group, index) => (
            <span
              key={index}
              className={cn(styles.group, index < 3 && styles.masked)}
              aria-hidden={index < 3}
            >
              {group}
            </span>
          ))}
        </span>

        <div className={styles.meta}>
          <div className={styles.metaPair}>
            <div>
              <Text variant="eyebrow" tone="onDarkMuted">
                {card.kind === 'agent' ? 'Issued to' : 'Card holder'}
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark">
                {card.kind === 'agent' ? card.label : card.holder}
              </Text>
            </div>
            <div>
              <Text variant="eyebrow" tone="onDarkMuted">
                Expires
              </Text>
              <Text variant="caption" weight="semibold" tone="onDark" numeric>
                {card.expiry}
              </Text>
            </div>
          </div>
          <NetworkMark network={card.network} />
        </div>
      </div>
    </div>
  )
}
